import {
	CameraTwoTone,
	DeleteOutlined,
	UploadOutlined,
} from "@ant-design/icons";
import { useEnvStore } from "@di-devs/utils-fe";
import type { RadioChangeEvent } from "antd";
import {
	Button,
	Card,
	Col,
	Collapse,
	Form,
	Input,
	Modal,
	message,
	Radio,
	Row,
	Space,
	Tabs,
	Upload,
} from "antd";
import type React from "react";
import { useEffect, useState } from "react";
import type { RichMenuTabGroupWithRichmenus } from "../../../../shared/types/richmenu.types";
import { getRichmenuTemplateImage } from "../../../assets";
import {
	useCreateRichMenuTabGroup,
	useUpdateRichMenuTabGroup,
} from "../../../richmenu-tab-group/hooks/useRichmenuTabGroupQueries";
import { MAX_RICHMENU_TABS } from "../../constants/richmenu.constants";
import { useUpdateRichMenuImage } from "../../hooks/useRichmenuQueries";
import type { RichMenuData } from "../../types/richmenu.types";
import { createDefaultRichMenu } from "../../utils/richmenu.utils";
import { RichMenuActionsCollapse } from "./RichMenuActionsCollapse";
// Import new clean components
import { RichMenuImageUpload } from "./RichMenuImageUpload";

interface RichMenuModalProps {
	visible: boolean;
	onCancel?: () => void;
	onClose?: () => void;
	editingTabGroup?: RichMenuTabGroupWithRichmenus | null;
	onSuccess?: (savedTabGroup?: any) => void;
	tabGroup?: RichMenuTabGroupWithRichmenus | null;
	templates?: any;
	uploadUrl?: string;
}

const TemplateRadio = Radio.Button;

export const RichMenuModal: React.FC<RichMenuModalProps> = ({
	visible,
	onCancel,
	onClose,
	editingTabGroup,
	onSuccess,
	tabGroup,
	templates,
	uploadUrl,
}) => {
	const { API_URL } = useEnvStore();

	const [form] = Form.useForm();
	const [activeTabKey, setActiveTabKey] = useState("0");
	const [uploadingStates, setUploadingStates] = useState<
		Record<number, boolean>
	>({});
	const [richMenus, setRichMenus] = useState<RichMenuData[]>([
		createDefaultRichMenu(0),
	]);

	const createMutation = useCreateRichMenuTabGroup();
	const updateMutation = useUpdateRichMenuTabGroup();
	const imageUploadMutation = useUpdateRichMenuImage();

	useEffect(() => {
		if (editingTabGroup) {
			form.setFieldsValue({
				name: editingTabGroup.name,
				displayPriority: editingTabGroup.displayPriority,
			});

			if (editingTabGroup.richmenus && editingTabGroup.richmenus.length > 0) {
				setRichMenus(
					editingTabGroup.richmenus.map((rm) => ({
						name: rm.name || "Rich Menu",
						chatBarText: rm.chatBarText || "メニュー",
						templateType: rm.templateType || "",
						areas: rm.areas || [],
						size: rm.size || { width: 1200, height: 405 },
						imageName: rm.imageName,
					})),
				);
			}
		} else {
			form.resetFields();
			setRichMenus([
				{
					name: "Rich Menu 1",
					chatBarText: "メニュー",
					templateType: "",
					areas: [],
					size: { width: 1200, height: 405 },
				},
			]);
		}
	}, [editingTabGroup, form, visible]);

	const handleSubmit = async (values: any) => {
		// Prevent submission if any upload is in progress
		const isAnyUploading = Object.values(uploadingStates).some(Boolean);
		if (isAnyUploading) {
			message.warning(
				"Please wait for all uploads to complete before submitting.",
			);
			return;
		}

		try {
			console.log(
				"handleSubmit called with richMenus:",
				richMenus.map((rm, index) => ({
					index,
					name: rm.name,
					hasImageFile: !!rm.imageFile,
					imageFileName: rm.imageFile?.name,
					imageFileSize: rm.imageFile?.size,
					imageFileType: rm.imageFile?.type,
					currentImageName: rm.imageName,
				})),
			);

			// Filter richmenu data and prepare for submission (following @tmp pattern)
			const filterRichMenuTabsFormData = (richMenuTabsData: any[]) => {
				return richMenuTabsData.map((richMenu: any, index: number) => {
					const isSelected = index === 0;
					return {
						...richMenu,
						selected: isSelected,
						name: richMenu.name || null,
						tabIndex: index,
						// Remove imageFile for server payload - but PRESERVE imageName
						imageFile: undefined,
						richmenuImage: undefined,
						// Keep existing imageName - don't set it to null/undefined
						imageName: richMenu.imageName, // Explicitly preserve imageName
					};
				});
			};

			const filteredRichMenuTabsData = filterRichMenuTabsFormData(richMenus);

			console.log(
				"Filtered rich menu data being sent to server:",
				filteredRichMenuTabsData.map((rm, index) => ({
					index,
					name: rm.name,
					preservedImageName: rm.imageName,
					hasImageFile: rm.imageFile !== undefined,
				})),
			);

			// Create/update the tab group first (without images)
			const tabGroupData = {
				name: values.name,
				displayPriority: values.displayPriority || "DEFAULT",
				richmenus: filteredRichMenuTabsData,
			};

			console.log(
				"Submitting tab group data:",
				JSON.stringify(tabGroupData, null, 2),
			);

			let result: any;
			if (editingTabGroup) {
				result = await updateMutation.mutateAsync({
					tabGroupId: editingTabGroup.groupId,
					data: tabGroupData,
				});
			} else {
				result = await createMutation.mutateAsync(tabGroupData);
			}

			// Check if result has success property and get created richmenu IDs
			if (
				result &&
				typeof result === "object" &&
				"success" in result &&
				result.success
			) {
				const createdRichmenus = result.data?.richmenus || [];

				// Now upload images for richmenus that have files (following @tmp pattern)
				await Promise.all(
					richMenus.map(async (rm, index) => {
						const createdRichmenu = createdRichmenus[index];

						console.log(`Rich Menu ${index} image check:`, {
							hasImageFile: !!rm.imageFile,
							imageFileName: rm.imageFile?.name,
							imageFileSize: rm.imageFile?.size,
							imageFileType: rm.imageFile?.type,
							createdRichmenuId: createdRichmenu?.id,
						});

						if (rm.imageFile && createdRichmenu?.id) {
							try {
								console.log(`Uploading image for Rich Menu ${index}:`, {
									richmenuId: createdRichmenu.id,
									fileName: rm.imageFile.name,
									fileSize: rm.imageFile.size,
									fileType: rm.imageFile.type,
								});

								await imageUploadMutation.mutateAsync({
									id: createdRichmenu.id,
									file: rm.imageFile,
								});
								message.success(`Image uploaded for ${rm.name}`);
							} catch (uploadError) {
								console.error("Image upload failed:", uploadError);
								message.warning(`Failed to upload image for ${rm.name}`);
							}
						} else {
							console.log(`Skipping image upload for Rich Menu ${index}:`, {
								reason: !rm.imageFile
									? "No image file"
									: "No created richmenu ID",
							});
						}
					}),
				);

				message.success(
					editingTabGroup
						? "Rich menu tab group updated successfully!"
						: "Rich menu tab group created successfully!",
				);

				// Call success callbacks with saved tab group info
				const savedTabGroup = result.data || { status: "DRAFT" }; // Default to DRAFT since new items are typically drafts
				if (typeof onSuccess === "function") {
					onSuccess(savedTabGroup);
				}
				const closeHandler = onClose || onCancel;
				if (typeof closeHandler === "function") {
					closeHandler();
				}
			} else {
				throw new Error(result?.message || "Operation failed");
			}
		} catch (error: any) {
			console.error("Error saving rich menu tab group:", error);
			console.error("Error details:", {
				message: error?.message,
				response: error?.response,
				status: error?.status,
				stack: error?.stack,
			});
			const errorMessage =
				error?.message || "Failed to save rich menu tab group";
			message.error(errorMessage);
		}
	};

	const onTabsChange = (newActiveKey: string) => {
		setActiveTabKey(newActiveKey);
	};

	const addTab = () => {
		if (richMenus.length < MAX_RICHMENU_TABS) {
			const newRichMenu = {
				name: `Rich Menu ${richMenus.length + 1}`,
				chatBarText: "メニュー",
				templateType: "",
				areas: [],
				size: { width: 1200, height: 405 },
			};
			setRichMenus([...richMenus, newRichMenu]);
			setActiveTabKey(String(richMenus.length));
		}
	};

	const removeTab = (targetKey: string) => {
		if (richMenus.length > 1) {
			const targetIndex = Number(targetKey);
			const newActiveKey =
				Number(activeTabKey) === 0 ? "0" : String(Number(activeTabKey) - 1);

			setRichMenus(richMenus.filter((_, i) => i !== targetIndex));
			setActiveTabKey(newActiveKey);
		}
	};

	const onTabsEdit = (
		targetKey: React.MouseEvent | React.KeyboardEvent | string,
		action: "add" | "remove",
	) => {
		if (action === "remove" && typeof targetKey === "string") {
			removeTab(targetKey);
		} else if (action === "add") {
			addTab();
		}
	};

	const removeRichMenu = (index: number) => {
		removeTab(String(index));
	};

	const updateRichMenu = (index: number, field: string, value: any) => {
		console.log("updateRichMenu called:", {
			index,
			field,
			value:
				field === "imageFile"
					? { name: value?.name, size: value?.size, type: value?.type }
					: value,
		});

		const newRichMenus = [...richMenus];
		(newRichMenus[index] as any)[field] = value;

		console.log(`Rich Menu ${index} after update:`, {
			field,
			hasImageFile: !!newRichMenus[index].imageFile,
			imageFileName: newRichMenus[index].imageFile?.name,
		});

		setRichMenus(newRichMenus);
	};

	const handleTemplateChange = (e: RadioChangeEvent, richMenuIndex: number) => {
		const templateName = e.target.value;
		// Find the template in the data
		const allTemplates = [
			...(templates?.compact || []),
			...(templates?.large || []),
		];
		const template = allTemplates.find((t: any) => t.name === templateName);

		if (template) {
			const newRichMenus = [...richMenus];
			newRichMenus[richMenuIndex] = {
				...newRichMenus[richMenuIndex],
				templateType: template.name,
				size: template.size,
				areas: template.areas || [],
			};
			setRichMenus(newRichMenus);
			message.success("Template applied successfully!");
		}
	};

	const TemplateRadioOption = ({
		templateSizeType,
	}: {
		templateSizeType: string;
	}) => (
		<div key={templateSizeType}>
			<div className="flex items-center space mb-2">
				<Row className="w-full" justify="space-between">
					<Col>
						<span style={{ fontSize: "18px", fontWeight: "500" }}>
							{templateSizeType === "large" ? "大：" : "小："}
						</span>
						<span
							style={{ fontSize: "14px", color: "#666", marginLeft: "8px" }}
						>
							{templateSizeType === "large"
								? "(リッチメニューのコンテンツを多く表示する場合におすすめです。)(1200px x 810px)"
								: "(トークルームにバランスよくリッチメニューを表示する場合におすすめです。)(1200px x 405px)"}
						</span>
					</Col>
				</Row>
			</div>
			<div>
				{templates?.[templateSizeType as keyof typeof templates]?.map(
					(template: any, templateIndex: number) => (
						<TemplateRadio
							key={templateIndex}
							value={template.name}
							style={{
								maxWidth: "240px",
								height: "auto",
								marginBottom: "8px",
								marginRight: "8px",
								padding: 0,
							}}
						>
							<div style={{ display: "flex", alignItems: "center" }}>
								<img
									src={
										getRichmenuTemplateImage(template.name) ||
										`${API_URL}/assets/richmenu-templates/${template.name}.png`
									}
									alt={template.name}
									style={{
										width: "190px",
										height: "auto",
										display: "block",
									}}
								/>
							</div>
						</TemplateRadio>
					),
				)}
			</div>
		</div>
	);

	const TemplateRadioGroup = ({
		richMenuTabIndex,
	}: {
		richMenuTabIndex: number;
	}) => (
		<Radio.Group
			optionType="button"
			buttonStyle="outline"
			size="large"
			value={richMenus[richMenuTabIndex]?.templateType}
			onChange={(e: RadioChangeEvent) =>
				handleTemplateChange(e, richMenuTabIndex)
			}
		>
			{Object.keys(templates || {})
				.reverse()
				?.map((key, index) => (
					<TemplateRadioOption key={index} templateSizeType={key} />
				))}
		</Radio.Group>
	);

	const TemplateCollapse = ({
		richMenuTabIndex,
	}: {
		richMenuTabIndex: number;
	}) => {
		const hasInitialTemplate = richMenus[richMenuTabIndex]?.templateType;
		return (
			<Collapse
				expandIconPosition="end"
				style={{ alignItems: "center" }}
				items={[
					{
						key: "template",
						label: (
							<div
								style={{
									display: "flex",
									alignItems: "center",
									gap: "8px",
								}}
							>
								<CameraTwoTone
									style={{
										fontSize: "16px",
										color: hasInitialTemplate ? "#1890ff" : "#d9d9d9",
									}}
								/>
								<span>テンプレート</span>
							</div>
						),
						children: (
							<TemplateRadioGroup richMenuTabIndex={richMenuTabIndex} />
						),
					},
				]}
			/>
		);
	};

	const tabItems = richMenus.map((richMenu, index) => ({
		key: index.toString(),
		label: (
			<Space>
				{richMenu.name || `Rich Menu ${index + 1}`}
				{richMenus.length > 1 && (
					<DeleteOutlined
						onClick={(e) => {
							e.stopPropagation();
							removeRichMenu(index);
						}}
						style={{ color: "#ff4d4f", fontSize: 12 }}
					/>
				)}
			</Space>
		),
		children: (
			<div>
				<Row gutter={[24, 16]}>
					<Col xs={24} md={12}>
						<Card title="Rich Menu Settings">
							<div
								style={{ display: "flex", flexDirection: "column", gap: 16 }}
							>
								<div>
									<label
										style={{
											display: "block",
											marginBottom: 8,
											fontWeight: 500,
										}}
									>
										Rich Menu Name
									</label>
									<Input
										value={richMenu.name}
										onChange={(e) => {
											if (uploadingStates[index]) {
												e.preventDefault();
												e.stopPropagation();
												return;
											}
											updateRichMenu(index, "name", e.target.value);
										}}
										onKeyDown={(e) => {
											if (uploadingStates[index]) {
												e.preventDefault();
												e.stopPropagation();
											}
										}}
										placeholder="Enter rich menu name"
										disabled={uploadingStates[index]}
									/>
								</div>

								<div>
									<label
										style={{
											display: "block",
											marginBottom: 8,
											fontWeight: 500,
										}}
									>
										Chat Bar Text
									</label>
									<Input
										value={richMenu.chatBarText}
										onChange={(e) => {
											if (uploadingStates[index]) {
												e.preventDefault();
												e.stopPropagation();
												return;
											}
											updateRichMenu(index, "chatBarText", e.target.value);
										}}
										onKeyDown={(e) => {
											if (uploadingStates[index]) {
												e.preventDefault();
												e.stopPropagation();
											}
										}}
										placeholder="Enter chat bar text"
										disabled={uploadingStates[index]}
									/>
								</div>

								<div>
									<label
										style={{
											display: "block",
											marginBottom: 8,
											fontWeight: 500,
										}}
									>
										Upload Image
									</label>
									<Upload
										beforeUpload={(file) => {
											// Prevent duplicate uploads
											if (uploadingStates[index]) {
												return false;
											}

											console.log(`File selected for Rich Menu ${index}:`, {
												fileName: file.name,
												fileSize: file.size,
												fileType: file.type,
												fileObject: file,
											});

											setUploadingStates((prev) => ({
												...prev,
												[index]: true,
											}));

											// Update with slight delay for better UX
											setTimeout(() => {
												console.log(
													`Storing file in Rich Menu ${index} state:`,
													file,
												);
												updateRichMenu(index, "imageFile", file);
												// DON'T clear imageName - preserve existing image name for updates
												// updateRichMenu(index, "imageName", null); // This was causing the issue!

												// Verify the file was stored
												const currentRichMenu = richMenus[index];
												console.log(`Rich Menu ${index} state after update:`, {
													hasImageFile: !!currentRichMenu?.imageFile,
													storedFileName: currentRichMenu?.imageFile?.name,
													preservedImageName: currentRichMenu?.imageName,
												});

												message.success(
													`Image ${file.name} selected successfully`,
												);
												setUploadingStates((prev) => ({
													...prev,
													[index]: false,
												}));
											}, 100);

											return false;
										}}
										maxCount={1}
										accept="image/*"
										showUploadList={false}
										disabled={uploadingStates[index]}
										onChange={(info) =>
											uploadingStates[index] && info.fileList.splice(0)
										}
									>
										<Button
											icon={<UploadOutlined />}
											loading={uploadingStates[index]}
											disabled={uploadingStates[index]}
										>
											{uploadingStates[index]
												? "Uploading..."
												: richMenu.imageFile || richMenu.imageName
													? "Change Image"
													: "Upload Image"}
										</Button>
									</Upload>
									{richMenu.imageFile && (
										<div style={{ marginTop: 8 }}>
											<span style={{ color: "#52c41a" }}>
												New image selected: {richMenu.imageFile.name}
											</span>
										</div>
									)}
									{!richMenu.imageFile && richMenu.imageName && (
										<div style={{ marginTop: 8 }}>
											<span style={{ color: "#1890ff" }}>
												Current image: {richMenu.imageName}
											</span>
										</div>
									)}
								</div>
							</div>
						</Card>
					</Col>

					<Col xs={24} md={12}>
						<Card title="Image Upload & Preview">
							<RichMenuImageUpload
								richMenuIndex={index}
								richMenu={richMenus[index]}
								updateRichMenu={updateRichMenu}
							/>
						</Card>
					</Col>
				</Row>

				<div style={{ marginTop: 16 }}>
					<TemplateCollapse richMenuTabIndex={index} />
				</div>

				<div style={{ marginTop: 16 }}>
					<label
						style={{
							display: "block",
							marginBottom: 8,
							fontWeight: 500,
						}}
					>
						Actions
					</label>
					<RichMenuActionsCollapse
						richMenuIndex={index}
						richMenu={richMenus[index]}
						updateRichMenu={updateRichMenu}
					/>
				</div>
			</div>
		),
	}));

	const isAnyUploading = Object.values(uploadingStates).some(Boolean);
	const uploadingCount = Object.values(uploadingStates).filter(Boolean).length;

	return (
		<Modal
			title={editingTabGroup ? "Edit Rich Menu" : "Create Rich Menu"}
			open={visible}
			onCancel={onClose || onCancel}
			onOk={() => form.submit()}
			okText={
				isAnyUploading
					? `Uploading... (${uploadingCount})`
					: editingTabGroup
						? "Update"
						: "Create"
			}
			okButtonProps={{
				loading: isAnyUploading,
				disabled: isAnyUploading,
			}}
			width={1200}
			style={{ top: 20 }}
		>
			<Form form={form} layout="vertical" onFinish={handleSubmit}>
				<Row gutter={16}>
					<Col xs={24} md={12}>
						<Form.Item
							name="name"
							label="Tab Group Name"
							rules={[
								{ required: true, message: "Please enter tab group name" },
							]}
						>
							<Input placeholder="Enter tab group name" />
						</Form.Item>
					</Col>
					<Col xs={24} md={12}>
						<Form.Item
							name="displayPriority"
							label="Display Priority"
							initialValue="DEFAULT"
						>
							<Radio.Group buttonStyle="solid">
								<Radio.Button value="DEFAULT">会員登録前</Radio.Button>
								<Radio.Button value="USER">会員登録後</Radio.Button>
							</Radio.Group>
						</Form.Item>
					</Col>
				</Row>

				<Tabs
					items={tabItems}
					type="editable-card"
					activeKey={activeTabKey}
					onChange={onTabsChange}
					onEdit={onTabsEdit}
					hideAdd={richMenus.length >= MAX_RICHMENU_TABS}
				/>
			</Form>
		</Modal>
	);
};
